import { useId } from "react";
import type React from "react";
import type { ScotomaSpot } from "shared/schema/src/types";

interface ScotomaToolProps {
	spots: ScotomaSpot[];
	selectedIndex: number | null;
	onSelect: (index: number | null) => void;
	onUpdateSelected: (
		patch: Partial<Pick<ScotomaSpot, "radius" | "intensity">>,
	) => void;
	onClearAll: () => void;
}

const MIN_RADIUS = 10;
const MAX_RADIUS = 150;

export const ScotomaTool: React.FC<ScotomaToolProps> = ({
	spots,
	selectedIndex,
	onSelect,
	onUpdateSelected,
	onClearAll,
}) => {
	const sizeId = useId();
	const opacityId = useId();

	// Guard against a stale index (e.g. after a reset/clear removed the spot).
	const sel =
		selectedIndex !== null && selectedIndex >= 0 && selectedIndex < spots.length
			? selectedIndex
			: null;
	const selectedSpot = sel !== null ? spots[sel] : undefined;
	const active =
		sel !== null && selectedSpot ? { index: sel, spot: selectedSpot } : null;

	return (
		<div className="p-4 space-y-3">
			<div className="mb-2 font-semibold">Scotoma Spots</div>

			<p className="text-sm text-gray-600">
				Click the image to place a blind spot, then adjust its size and darkness
				below. Click a spot to re-select it; right-click a spot to remove it.
			</p>

			{active ? (
				<div className="space-y-4 rounded border border-gray-200 p-3">
					<div className="text-sm font-medium">Spot {active.index + 1}</div>

					<div>
						<label className="mb-1 block text-sm" htmlFor={sizeId}>
							Size: {Math.round(active.spot.radius)} px
						</label>
						<div className="flex items-center gap-2">
							<button
								type="button"
								aria-label="Decrease size"
								className="h-11 w-11 shrink-0 rounded bg-blue-500 text-white"
								onClick={() =>
									onUpdateSelected({
										radius: Math.max(MIN_RADIUS, active.spot.radius - 5),
									})
								}
							>
								−
							</button>
							<input
								id={sizeId}
								type="range"
								min={MIN_RADIUS}
								max={MAX_RADIUS}
								step={1}
								value={active.spot.radius}
								onChange={(e) =>
									onUpdateSelected({ radius: Number(e.target.value) })
								}
								className="w-full"
							/>
							<button
								type="button"
								aria-label="Increase size"
								className="h-11 w-11 shrink-0 rounded bg-blue-500 text-white"
								onClick={() =>
									onUpdateSelected({
										radius: Math.min(MAX_RADIUS, active.spot.radius + 5),
									})
								}
							>
								+
							</button>
						</div>
					</div>

					<div>
						<label className="mb-1 block text-sm" htmlFor={opacityId}>
							Darkness: {Math.round(active.spot.intensity * 100)}%
						</label>
						<div className="flex items-center gap-2">
							<button
								type="button"
								aria-label="Decrease darkness"
								className="h-11 w-11 shrink-0 rounded bg-blue-500 text-white"
								onClick={() =>
									onUpdateSelected({
										intensity: Math.max(0, active.spot.intensity - 0.05),
									})
								}
							>
								−
							</button>
							<input
								id={opacityId}
								type="range"
								min={0}
								max={1}
								step={0.05}
								value={active.spot.intensity}
								onChange={(e) =>
									onUpdateSelected({ intensity: Number(e.target.value) })
								}
								className="w-full"
							/>
							<button
								type="button"
								aria-label="Increase darkness"
								className="h-11 w-11 shrink-0 rounded bg-blue-500 text-white"
								onClick={() =>
									onUpdateSelected({
										intensity: Math.min(1, active.spot.intensity + 0.05),
									})
								}
							>
								+
							</button>
						</div>
					</div>
				</div>
			) : (
				<p className="rounded border border-dashed border-gray-300 p-3 text-sm text-gray-500">
					No spot selected. Click the image to place one.
				</p>
			)}

			{spots.length > 0 && (
				<ul className="space-y-1">
					{spots.map((spot, i) => (
						<li key={`scotoma-${spot.x}-${spot.y}-${i}`}>
							<button
								type="button"
								className={`w-full rounded px-2 py-2 text-left text-xs ${
									i === sel
										? "bg-blue-100 text-blue-800"
										: "text-gray-600 hover:bg-gray-100"
								}`}
								onClick={(e) => {
									e.stopPropagation();
									onSelect(i);
								}}
							>
								Spot {i + 1}: ({spot.x.toFixed(0)}, {spot.y.toFixed(0)}) ·{" "}
								{Math.round(spot.radius)} px ·{" "}
								{Math.round(spot.intensity * 100)}%
							</button>
						</li>
					))}
				</ul>
			)}

			<button
				type="button"
				className="w-full rounded bg-red-500 px-3 py-2 text-sm text-white transition-colors hover:bg-red-600"
				onClick={(e) => {
					e.stopPropagation();
					onClearAll();
				}}
			>
				Clear All Spots
			</button>
		</div>
	);
};
